import { supabase } from '../lib/supabase';

export type TipoPedidoEsic = 'informacao' | 'documento' | 'dado' | 'outro';
export type FormaRecebimento = 'plataforma' | 'email' | 'postal' | 'presencial';

export const TIPO_PEDIDO_LABEL: Record<TipoPedidoEsic, string> = {
  informacao: 'Informação',
  documento: 'Cópia de documento',
  dado: 'Dados estruturados (CSV, etc)',
  outro: 'Outro',
};

export const FORMA_RECEBIMENTO_LABEL: Record<FormaRecebimento, string> = {
  plataforma: 'Pela plataforma (recomendado)',
  email: 'Por e-mail',
  postal: 'Carta postal (correio)',
  presencial: 'Retirada presencial',
};

export interface AssuntoEsic {
  id: string;
  nome: string;
  descricao: string | null;
  prazo_dias: number | null;
}

export interface RecursoEsic {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  data_abertura: string;
  data_conclusao: string | null;
  documento_pai_id: string | null;
  instancia_recurso: number | null;
  nivel: number;
}

export interface IndicadoresEsic {
  ano: number;
  total_pedidos: number;
  deferidos: number;
  indeferidos: number;
  respondidos_no_prazo: number;
  respondidos_fora_do_prazo: number;
  prorrogados: number;
  tempo_medio_resposta_horas: number;
  recursos: { instancia_1: number; instancia_2: number; instancia_3: number; instancia_4: number };
  distribuicao_por_tipo: Record<string, number>;
}

export const esicPortalService = {
  async listarAssuntos(estabelecimentoId: string): Promise<AssuntoEsic[]> {
    const { data, error } = await supabase
      .from('assuntos')
      .select('id, nome, descricao, prazo_dias')
      .eq('estabelecimento_id', estabelecimentoId)
      .eq('modulo_codigo', 'esic')
      .eq('publica', true)
      .eq('ativo', true)
      .is('deletado_em', null)
      .order('nome');
    if (error) throw error;
    return (data ?? []) as AssuntoEsic[];
  },

  async abrir(args: {
    estabelecimento_id: string;
    cidadao_id: string;
    assunto_id: string;
    tipo_pedido: TipoPedidoEsic;
    forma_recebimento: FormaRecebimento;
    titulo: string;
    descricao: string;
    justificativa?: string;
  }): Promise<{ id: string; numero: string; hash_publico: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: args.estabelecimento_id,
        modulo_codigo: 'esic',
        assunto_id: args.assunto_id,
        titulo: args.titulo,
        descricao: args.descricao,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: 'interna',
        solicitante_cidadao_id: args.cidadao_id,
        tipo_pedido_esic: args.tipo_pedido,
        campos_customizados: {
          tipo_pedido_esic: args.tipo_pedido,
          forma_recebimento: args.forma_recebimento,
          ...(args.justificativa ? { justificativa: args.justificativa } : {}),
        },
      })
      .select('id, numero, hash_publico')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string; hash_publico: string };
  },

  async recorrer(documentoPaiId: string, justificativa: string): Promise<{ documento_id: string; numero: string; instancia: number }> {
    const { data, error } = await supabase.rpc('esic_recorrer', {
      p_documento_pai_id: documentoPaiId,
      p_justificativa: justificativa,
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'erro' in data) {
      throw new Error(String((data as { erro: string }).erro));
    }
    return data as { documento_id: string; numero: string; instancia: number };
  },

  async listarRecursos(documentoId: string): Promise<RecursoEsic[]> {
    const { data, error } = await supabase.rpc('esic_listar_recursos', { p_documento_id: documentoId });
    if (error) throw error;
    return (data ?? []) as RecursoEsic[];
  },

  async indicadores(estabelecimentoId: string, ano?: number): Promise<IndicadoresEsic> {
    const { data, error } = await supabase.rpc('esic_indicadores_publico', {
      p_estabelecimento_id: estabelecimentoId,
      p_ano: ano ?? null,
    });
    if (error) throw error;
    return data as IndicadoresEsic;
  },
};
